import axios from "axios"
import qs from "qs"
// constants
import { REACT_APP_STRAPI_API_URL } from "constants/endpoints"
// types
import { Pagination } from "types/api"
import { CourseProgress, CourseProgressStatuses, EpisodePosition } from "types/courseProgress"
import { Locales, Localization } from "types/courses"
import { getLocaleEpisodeByEpisodeDocumentId } from "./episodes"
import { Language } from "types/i18n"
import i18n from "i18n"

type GetSuperCourseProgressRequest = {
  superCourseDocumentId: string
}

export type GetSuperCourseProgressResponse = {
  data: {
    id: number
    superEpisodeWatchedCount: number
    watchStatus: string
    courseWatchedDate: string
    cmeTestPassed: boolean
    cmeTestPassedDate: string
    createdAt: string
    updatedAt: string
    superCourse: { data: { id: number } }
    episodePosition: {
      id: number
      currentPosition: number
      maximumPosition: number
      isWatched: true
      createdAt: string
      updatedAt: string
      episode: {
        id: number
        locale: string
        documentId: string
        localizations: Localization[]
      }
    }
  }[]
  meta: Pagination
}

export const COURSE_WATCH_KEYS = Object.freeze({
  GET_SUPER_COURSE_PROGRESS: "superCourseProgress",
  GET_SUPER_COURSES_PROGRESS_WATCHED: "superCourseProgressWatched",
  GET_SUPER_COURSES_IN_PROGRESS: "superCoursesInProgress",
  GET_SUPER_COURSES_NOT_SEEN: "superCoursesNotSeen",
  GET_ALL_LOCALE_EPISODES: "allLocaleEpisodes",
})

export const getSuperCourseProgress = async ({ superCourseDocumentId }: GetSuperCourseProgressRequest) => {
  const params = qs.stringify(
    {
      filters: {
        superCourse: {
          documentId: {
            $eq: superCourseDocumentId,
          },
        },
      },
      populate: {
        superCourse: {
          fields: ["id"],
        },
        episodePosition: {
          fields: ["id"],
          populate: {
            episode: {
              fields: ["id", "locale"],
            },
          },
        },
      },
    },
    { encodeValuesOnly: true }
  )

  const url = `${REACT_APP_STRAPI_API_URL}super-course-progresses?${params}`

  const response = await axios.get<GetSuperCourseProgressResponse>(url)

  if (response.data.data.length > 0) {
    const episodeDocumentId = response.data.data[0]?.episodePosition?.episode?.documentId

    const episodes = [] as Localization[]

    const locales: Language[] = ["en", "de"]

    const otherLocale = locales.find((locale) => locale !== (i18n.language.slice(0, 2) as Language))

    const episode = await getLocaleEpisodeByEpisodeDocumentId(episodeDocumentId, otherLocale)
    episodes.push({
      id: episode.id,
      locale: episode.locale as Locales,
      kalturaVideoId: episode.kalturaVideoId,
      documentId: episode.documentId,
    })

    response.data.data[0].episodePosition.episode.localizations = episodes
  }

  return response.data
}

type GetSuperCourseProgressesByStatusRequest = {
  watchStatus: CourseProgressStatuses
}

export type GetSuperCourseProgressesByStatusResponse = {
  data: CourseProgress<Pick<EpisodePosition, "maximumPosition">>[]
  meta: Pagination
}

export const getSuperCourseProgressesByStatus = async ({ watchStatus }: GetSuperCourseProgressesByStatusRequest) => {
  const params = qs.stringify(
    {
      filters: {
        watchStatus: {
          $eq: watchStatus,
        },
      },
      populate: {
        superCourse: {
          fields: ["id"],
          populate: {
            courses: {
              fields: [
                "publishedAt",
                "firstPublishedAt",
                "courseType",
                "courseLength",
                "cmePoints",
                "cmePointsText",
                "courseName",
                "certified",
                "hashCourseCover",
                "hashCourseCoverLandscape",
                "locale",
              ],
              populate: {
                cover: {
                  fields: ["ext", "url"],
                },
                coverLandscape: {
                  fields: ["ext", "url"],
                },
                speaker: {
                  fields: ["speakerTitle", "speakerFirstName", "speakerLastName"],
                },
                episodes: {
                  fields: "id",
                },
                specialties: {
                  fields: ["name"],
                },
                relatedSpecialties: {
                  fields: ["name"],
                },
                cmeTest: {
                  fields: ["externalCertificate", "externalCertificatePrice"],
                },
              },
            },
          },
        },
        episodePosition: {
          fields: "maximumPosition",
        },
      },
      sort: { updatedAt: "DESC" },
    },
    { encodeValuesOnly: true }
  )

  const url = `${REACT_APP_STRAPI_API_URL}super-course-progresses?${params}`

  const response = await axios.get<GetSuperCourseProgressesByStatusResponse>(url)

  return response.data
}

export type GetNotSeenSuperCoursesResponse = { id: number }[]

export const getNotSeenSuperCourses = async () => {
  const url = `${REACT_APP_STRAPI_API_URL}not-seen-super-courses`

  const response = await axios.get<GetNotSeenSuperCoursesResponse>(url)

  return response.data
}
